import type { Mode } from "@eldrun/content";
import { deriveSeed } from "@eldrun/shared";
import { BIOME, classifyBiome } from "./biomes.ts";
import { conservativeParams, MAX_ATTEMPTS, PLAN_SIDE, PLAN_SPACING, terrainParams, WORLD_HALF, type TerrainParams } from "./params.ts";
import { planPois, poiStamps, type Poi, type PoiPlan } from "./pois.ts";
import { SEA_T, SERRA_T, TerrainField } from "./terrain.ts";

const CELL_AREA = PLAN_SPACING * PLAN_SPACING;
const DIAGONAL = PLAN_SPACING * 1.4142135623730951;

export interface PlanStats {
  landCells: number;
  landArea: number;
  /** Fração da terra no maior componente conexo. */
  mainLandFraction: number;
  /** Células por bioma, indexadas por `BIOME`. */
  biomeCells: number[];
  maxHeight: number;
}

export interface Plan {
  seed: number;
  mode: Mode;
  /** Tentativa aceita (0..MAX_ATTEMPTS−1), ou MAX_ATTEMPTS para os parâmetros conservadores. */
  attempt: number;
  terrainSeed: number;
  params: TerrainParams;
  /** Elevação bruta que vira o nível do mar. */
  seaBaseT: number;
  /** Escala da elevação acima do mar. */
  landScale: number;
  field: TerrainField;
  /** Grades PLAN_SIDE × PLAN_SIDE (índice = iz·PLAN_SIDE + ix). */
  heights: Float32Array;
  moisture: Float32Array;
  slope: Float32Array;
  coastDistance: Float32Array;
  landDistance: Float32Array;
  biome: Uint8Array;
  /** 1 para células do maior componente de terra. */
  mainLand: Uint8Array;
  stats: PlanStats;
  /** Motivos da rejeição de cada tentativa recusada (diagnóstico). */
  rejections: string[];
  /** Pontos de interesse (doc 04): o campo já tem os carimbos deles. */
  pois: Poi[];
  /** Rumo da travessia (do centro de massa para os destroços). */
  heading: { x: number; z: number };
}

export const planCoord = (i: number): number => -WORLD_HALF + i * PLAN_SPACING;

/** Limites mínimos de cobertura por bioma (fração da área emersa). */
export const VALIDATION = {
  mainLandFraction: 0.85,
  mata: 0.08,
  cerrado: 0.08,
  serra: 0.05,
  praia: 0.01,
} as const;

/** Fração da terra que deve ficar acima de ~45 m (serra por altitude). */
const SERRA_LAND_FRACTION = 0.14;

/**
 * Escolhe nível do mar e escala por percentis da elevação bruta: a área emersa fica igual ao
 * alvo e a fração de serra fica estável entre seeds, sem depender de outros chunks.
 */
function chooseRemap(baseT: Float64Array, targetCells: number): { seaBaseT: number; landScale: number } {
  const sorted = Float64Array.from(baseT).sort();
  const n = sorted.length;
  const seaIndex = Math.max(0, Math.min(n - 1, n - targetCells));
  const seaBaseT = sorted[seaIndex] as number;
  const serraIndex = Math.min(n - 1, n - Math.max(1, Math.round(targetCells * SERRA_LAND_FRACTION)));
  const serraBaseT = sorted[serraIndex] as number;
  const span = serraBaseT - seaBaseT;
  const raw = span > 1e-6 ? (SERRA_T - SEA_T) / span : 1;
  return { seaBaseT, landScale: raw < 0.5 ? 0.5 : raw > 4 ? 4 : raw };
}

/**
 * Distância até as células-fonte por transformada de chanfro em duas passadas
 * (determinística, O(n)): `toWater` mede até o mar; caso contrário, até a terra.
 */
function distanceTransform(heights: Float32Array, toWater: boolean): Float32Array {
  const n = PLAN_SIDE;
  const dist = new Float32Array(n * n);
  const far = 1e9;
  for (let i = 0; i < dist.length; i++) dist[i] = ((heights[i] as number) < 0) === toWater ? 0 : far;
  const relax = (i: number, j: number, cost: number) => {
    const candidate = (dist[j] as number) + cost;
    if (candidate < (dist[i] as number)) dist[i] = candidate;
  };
  for (let z = 0; z < n; z++) {
    for (let x = 0; x < n; x++) {
      const i = z * n + x;
      if (x > 0) relax(i, i - 1, PLAN_SPACING);
      if (z > 0) {
        relax(i, i - n, PLAN_SPACING);
        if (x > 0) relax(i, i - n - 1, DIAGONAL);
        if (x < n - 1) relax(i, i - n + 1, DIAGONAL);
      }
    }
  }
  for (let z = n - 1; z >= 0; z--) {
    for (let x = n - 1; x >= 0; x--) {
      const i = z * n + x;
      if (x < n - 1) relax(i, i + 1, PLAN_SPACING);
      if (z < n - 1) {
        relax(i, i + n, PLAN_SPACING);
        if (x < n - 1) relax(i, i + n + 1, DIAGONAL);
        if (x > 0) relax(i, i + n - 1, DIAGONAL);
      }
    }
  }
  return dist;
}

/** Marca o maior componente de terra (vizinhança 4) e devolve seu tamanho. */
function largestLandComponent(heights: Float32Array, out: Uint8Array): number {
  const n = PLAN_SIDE;
  const label = new Int32Array(n * n).fill(-1);
  const stack = new Int32Array(n * n);
  let best = -1;
  let bestSize = 0;
  let next = 0;
  for (let start = 0; start < label.length; start++) {
    if (label[start] !== -1 || (heights[start] as number) < 0) continue;
    const id = next++;
    let size = 0;
    let top = 0;
    stack[top++] = start;
    label[start] = id;
    while (top > 0) {
      const i = stack[--top] as number;
      size++;
      const x = i % n;
      const neighbors = [x > 0 ? i - 1 : -1, x < n - 1 ? i + 1 : -1, i - n, i + n];
      for (const j of neighbors) {
        if (j < 0 || j >= label.length || label[j] !== -1 || (heights[j] as number) < 0) continue;
        label[j] = id;
        stack[top++] = j;
      }
    }
    if (size > bestSize) {
      bestSize = size;
      best = id;
    }
  }
  for (let i = 0; i < label.length; i++) out[i] = label[i] === best ? 1 : 0;
  return bestSize;
}

function buildAttempt(seed: number, mode: Mode, attempt: number, params: TerrainParams): Plan {
  const n = PLAN_SIDE;
  const terrainSeed = attempt === 0 ? seed : deriveSeed(seed, "ilha", attempt);
  const field = new TerrainField(terrainSeed, params);

  const baseT = new Float64Array(n * n);
  for (let z = 0; z < n; z++) {
    for (let x = 0; x < n; x++) baseT[z * n + x] = field.baseT(planCoord(x), planCoord(z));
  }
  const { seaBaseT, landScale } = chooseRemap(baseT, Math.round(params.targetLandArea / CELL_AREA));
  field.seaBaseT = seaBaseT;
  field.landScale = landScale;

  const heights = new Float32Array(n * n);
  for (let i = 0; i < heights.length; i++) heights[i] = field.heightFromBase(baseT[i] as number);

  const slope = new Float32Array(n * n);
  for (let z = 0; z < n; z++) {
    for (let x = 0; x < n; x++) {
      const i = z * n + x;
      const hx = (heights[z * n + Math.min(n - 1, x + 1)] as number) - (heights[z * n + Math.max(0, x - 1)] as number);
      const hz = (heights[Math.min(n - 1, z + 1) * n + x] as number) - (heights[Math.max(0, z - 1) * n + x] as number);
      slope[i] = Math.sqrt(hx * hx + hz * hz) / (2 * PLAN_SPACING);
    }
  }

  const coastDistance = distanceTransform(heights, true);
  const landDistance = distanceTransform(heights, false);
  const moisture = new Float32Array(n * n);
  const biome = new Uint8Array(n * n);
  const biomeCells = [0, 0, 0, 0, 0, 0];
  let landCells = 0;
  let maxHeight = -Infinity;
  for (let z = 0; z < n; z++) {
    for (let x = 0; x < n; x++) {
      const i = z * n + x;
      const y = heights[i] as number;
      const m = field.moisture(planCoord(x), planCoord(z), y, coastDistance[i] as number);
      moisture[i] = m;
      const b = classifyBiome({
        y,
        moisture: m,
        slope: slope[i] as number,
        coastDistance: coastDistance[i] as number,
        landDistance: landDistance[i] as number,
      });
      biome[i] = b;
      biomeCells[b] = (biomeCells[b] as number) + 1;
      if (y >= 0) landCells++;
      if (y > maxHeight) maxHeight = y;
    }
  }

  const mainLand = new Uint8Array(n * n);
  const mainSize = largestLandComponent(heights, mainLand);
  return {
    seed,
    mode,
    attempt,
    terrainSeed,
    params,
    seaBaseT,
    landScale,
    field,
    heights,
    moisture,
    slope,
    coastDistance,
    landDistance,
    biome,
    mainLand,
    stats: {
      landCells,
      landArea: landCells * CELL_AREA,
      mainLandFraction: landCells > 0 ? mainSize / landCells : 0,
      biomeCells,
      maxHeight,
    },
    rejections: [],
    pois: [],
    heading: { x: 1, z: 0 },
  };
}

/** Motivos pelos quais a ilha não serve; lista vazia = aprovada. */
export function validatePlan(stats: PlanStats): string[] {
  const reasons: string[] = [];
  if (stats.mainLandFraction < VALIDATION.mainLandFraction) {
    reasons.push(`terra principal ${(stats.mainLandFraction * 100).toFixed(1)}%`);
  }
  const land = stats.landCells || 1;
  for (const name of ["mata", "cerrado", "serra", "praia"] as const) {
    const fraction = (stats.biomeCells[BIOME[name]] as number) / land;
    if (fraction < VALIDATION[name]) reasons.push(`${name} ${(fraction * 100).toFixed(1)}%`);
  }
  return reasons;
}

/**
 * Planeja a ilha da seed: tenta até MAX_ATTEMPTS variações e, se nenhuma passar na validação,
 * usa parâmetros conservadores. Função pura: mesma seed e modo, mesmo plano.
 */
export function planWorld(seed: number, mode: Mode): Plan {
  const rejections: string[] = [];
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const plan = buildAttempt(seed, mode, attempt, terrainParams(mode));
    const reasons = validatePlan(plan.stats);
    if (reasons.length === 0) {
      // A ilha só vale com todos os POIs nos mínimos da regra (doc 04): senão, a próxima tentativa.
      const result = placePois(plan, POI_SUBS);
      if (result.missing.length === 0 && result.short.length === 0) return { ...plan, rejections };
      reasons.push(`POIs sem lugar: ${[...result.missing, ...result.short].join(", ")}`);
    }
    rejections.push(`tentativa ${attempt}: ${reasons.join(", ")}`);
  }
  // Parâmetros conservadores: mais sub-seeds; os não obrigatórios podem ficar abaixo do mínimo, os
  // obrigatórios nunca (sem eles, não há partida: a geração falha em vez de seguir incompleta).
  const fallback = buildAttempt(seed, mode, MAX_ATTEMPTS, conservativeParams(mode));
  const result = placePois(fallback, FALLBACK_SUBS);
  if (result.missing.length > 0) throw new Error(`ilha ${seed} (${mode}) sem POIs obrigatórios: ${result.missing.join(", ")}`);
  if (result.short.length > 0) rejections.push(`parâmetros conservadores: POIs abaixo do mínimo: ${result.short.join(", ")}`);
  return { ...fallback, rejections };
}

/** Sub-seeds para os POIs antes de desistir da tentativa de terreno (e na conservadora). */
const POI_SUBS = 4;
const FALLBACK_SUBS = 32;

/**
 * Planeja os POIs (com até `subs` sub-seeds, parando na primeira completa), grava-os no plano e
 * instala os carimbos no campo. Devolve o que faltou na melhor sub-seed.
 */
function placePois(plan: Plan, subs: number): { missing: string[]; short: string[] } {
  const score = (p: PoiPlan) => p.missing.length * 100 + p.short.length;
  let bestPlan = planPois(plan, plan.field, plan.params.radius, 0);
  for (let sub = 1; sub < subs && score(bestPlan) > 0; sub++) {
    const next = planPois(plan, plan.field, plan.params.radius, sub);
    if (score(next) < score(bestPlan)) bestPlan = next;
  }
  plan.pois = bestPlan.pois;
  plan.heading = bestPlan.heading;
  plan.field.setStamps(poiStamps(bestPlan.pois));
  return { missing: bestPlan.missing, short: bestPlan.short };
}

/** Amostra bilinear de uma grade do plano em coordenadas de mundo (uso cosmético: cores, minimapa). */
export function samplePlanGrid(grid: Float32Array, x: number, z: number): number {
  const n = PLAN_SIDE;
  const gx = Math.min(n - 1, Math.max(0, (x + WORLD_HALF) / PLAN_SPACING));
  const gz = Math.min(n - 1, Math.max(0, (z + WORLD_HALF) / PLAN_SPACING));
  // Na borda superior a célula é a última (n − 2) com fração 1, alcançando o valor exato.
  const ix = Math.min(n - 2, Math.floor(gx));
  const iz = Math.min(n - 2, Math.floor(gz));
  const fx = gx - ix;
  const fz = gz - iz;
  const i = iz * n + ix;
  const a = grid[i] as number;
  const b = grid[i + 1] as number;
  const c = grid[i + n] as number;
  const d = grid[i + n + 1] as number;
  return a + (b - a) * fx + (c - a) * fz + (a - b - c + d) * fx * fz;
}
